import React, {useEffect, useState} from "react"
import {Icon} from "components"
import {useModel} from "hooks"
import {get} from "react-hook-form"
import {useTranslation} from "react-i18next"
import {useHistory} from "react-router-dom"
import {instructionalService} from "services"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {routing} from "helpers/routing"
import {KlassappTableProps} from "types/common"
import {TermDetails} from "types/terms"
import {KlassappTableHOC} from "HOC"
import {isEmpty} from "lodash"
import {FilterKey} from "types/filter"
import {formatDateOnly, handleError, roundTwoDigits} from "helpers"
import TermAndCoursesFilter from "../TermAndCoursesFilter"
import {TermAndCoursesTabs} from ".."

type Props = KlassappTableProps & {
    terms: TermDetails[]
}

const CoursesTab = (props: Props) => {
    const {
        dispatch,
        dispatchFunc,
        page,
        terms,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        isLoading,
        isLoadedTableFuncs,
        isShowTableHeaderAction
    } = props
    const {t} = useTranslation(["course"])
    const model = useModel()
    const history = useHistory()

    const storageData = model.getStorageFilter(FilterKey.RegistrarTermAndCourses)

    const [textSearch, setTextSearch] = useState<string>(!isEmpty(storageData) ? storageData.search : "")
    const [tempFilter, setTempFilter] = useState(
        !isEmpty(storageData?.filter)
            ? storageData.filter
            : {
                  campus: null,
                  instructor: null
              }
    )
    const [filter, setFilter] = useState(
        !isEmpty(storageData?.filter)
            ? storageData.filter
            : {
                  campus: null,
                  instructor: null
              }
    )

    const getFields = () => {
        return [
            "Term Course Id",
            t("course.name"),
            "Instructor",
            "Start Date",
            "End Date",
            t("course.lastLesson"),
            t("course.attendance"),
            "No. Student"
        ]
    }

    const getPageTitle = () => {
        return "Course"
    }

    const getColumns = () => {
        return [
            {
                title: "Term Course Id",
                field: "termCourseCode",
                sortable: true
            },
            {
                title: t("course.name"),
                field: "courseName",
                style: {width: "250px"},
                sortable: true
            },
            {
                title: "Instructor",
                field: "instructors",
                sortable: true
            },
            {
                title: "Start Date",
                field: "startDate",
                sortable: true
            },
            {
                title: "End Date",
                field: "endDate",
                sortable: true
            },
            {
                title: t("course.lastLesson"),
                field: "lastLessonDate",
                sortable: true
            },
            {
                title: t("course.attendance"),
                field: "attendancePercentage",
                sortable: true
            },
            {
                title: "No. Student",
                field: "numberOfStudent",
                sortable: true
            }
        ]
    }

    const renderActionHtml = (item) => {
        if (item.attendance?.actionNeededIds?.length > 0) {
            return <Icon icon="ALERT" />
        }
        return null
    }

    const getData = async () => {
        dispatch({isLoading: true})
        try {
            const params: any = {
                range: {page, pageSize},
                filter: {isPracticalOrClinicalServices: false}
            }
            params.filter.termIds = terms.map((item) => item.id)

            params.sort = orderField?.field
                ? {
                      orderBy: orderField.field,
                      orderDir: orderField.order
                  }
                : undefined

            if (filter.instructor && filter.instructor.length) {
                params.filter.instructorProfileIds = filter.instructor.map((item) => item.profileId)
            }
            if (filter.campus && filter.campus.length) {
                params.filter.campusIds = filter.campus.map((item) => item.id)
            }
            if (textSearch && textSearch !== "") {
                params.filter.search = textSearch
            }
            const profileId = get(model, "user.profiles[0].id")
            if (!model.isStaffOrAdmin() && profileId) {
                params.filter.studentProfileIds = [profileId]
            }

            const {data, total} = await instructionalService.getMyCourse(params)
            const myCourses = data.map((item: any, index: number) => {
                item.id = index + 1
                item.lastLessonDate = formatDateOnly(item?.lastLessonDate, model.getUserDateFormat())
                item.attendancePercentage = item.attendancePercentage
                    ? `${roundTwoDigits(item.attendancePercentage)}%`
                    : "0%"
                item.actionHtml = renderActionHtml(item)
                item.numberOfStudent = item.numberOfStudent.toString()
                item.startDate = formatDateOnly(item.startDate, model.getUserDateFormat())
                item.endDate = formatDateOnly(item.endDate, model.getUserDateFormat())
                return item
            })
            dispatch({data: myCourses, total})
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onClickRowItem = (row) => {
        history.push(routing.registrar.course(row.courseId, row.termId))
    }

    useEffect(() => {
        dispatch({isClassComponent: false})

        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (terms?.length && isLoadedTableFuncs) {
            getData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadedTableFuncs, page, pageSize, textSearch, terms, filter, orderField])

    useEffect(() => {
        props.updateTableHeaderActions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    return (
        <div>
            <TermAndCoursesFilter
                type={TermAndCoursesTabs.TermCoursesCourses}
                tempFilter={tempFilter}
                setTempFilter={setTempFilter}
                textSearch={textSearch}
                setFilter={setFilter}
                filter={filter}
                setTextSearch={setTextSearch}
            />
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                menuActions={[]}
                isLoading={isLoading}
                allFields={allFields}
                isShowCheckedColumn={false}
                orderField={orderField}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
            />
        </div>
    )
}

export default KlassappTableHOC(CoursesTab, {
    page: 1,
    pageSize: 100
})
